
// client/src/components/media/NewPost.jsx
// Create a new post - ONLY posts media uploaded in THIS session

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import PostChoiceModal from "./PostChoiceModal";
import NativeCameraCapture from "./NativeCameraCapture";
import ImageUploadModal from "./ImageUploadModal";
import { FaArrowLeft, FaCamera, FaUpload, FaPaperPlane, FaTrash, FaClosedCaptioning, FaFont } from "react-icons/fa";
import SubtitleEditor from "./SubtitleEditor";
import { sessionRequestConfig } from "../../services/sessionLocal";

// Helpers
const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith('/') ? '' : '/'}${p}`;
const toPublic = (p) => (p?.startsWith("/public/") ? p : p?.startsWith("/tenants/") ? `/public${p}` : p);
const isImage = (f) =>
  (f?.mimetype?.startsWith?.("image/")) || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(f?.path || f || "");
const isVideo = (f) =>
  (f?.mimetype?.startsWith?.("video/")) || /\.(mp4|mov|webm|ogg|avi|mkv)$/i.test(f?.path || f || "");

const NewPost = () => {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || (typeof window !== "undefined" ? window.location.origin : "http://localhost:5000");
  const cfg = sessionRequestConfig();

  const [timelineEvent, setTimelineEvent] = useState(null);
  
  // Track ONLY files uploaded in this session
  const [sessionFiles, setSessionFiles] = useState([]); // Array of {name, url, type}
  
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [postChoiceOpen, setPostChoiceOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(false);

  // Video subtitle editor state
  const [editingVideoIdx, setEditingVideoIdx] = useState(null);
  const [videoSubtitles, setVideoSubtitles] = useState([]); // VTT URLs per video index

  // Load Timeline event only (don't load any existing media)
  useEffect(() => {
    if (!tenant) return;
    let alive = true;

    (async () => {
      try {
        const { data: timelineData } = await axios.get(
          `/api/events/${tenant}/timeline/ensure`,
          cfg
        );
        if (!alive) return;

        const evt = timelineData?.event;
        if (!evt?.id) throw new Error("Failed to get Timeline event");

        setTimelineEvent(evt);
      } catch (err) {
        console.error("Error loading:", err);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [tenant]); // eslint-disable-line

  // Handle Upload - track which files were uploaded
  const handleUploadComplete = async (files) => {
    setShowUpload(false);

    if (!files?.length || !timelineEvent?.id) return;

    try {
      // Upload files
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f, f.name));

      const response = await axios.post(
        `/api/events/${tenant}/events/${timelineEvent.id}/uploadimage`,
        fd,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      // Use the uploaded file paths returned by the server
      const uploaded = response.data?.uploaded || [];

      const newlyUploaded = uploaded.map(media => ({
        name: media.filename || media.path?.split('/').pop() || 'upload',
        url: media.path,
        type: media.mimetype || 'image/jpeg',
        mimetype: media.mimetype || 'image/jpeg'
      }));

      console.log("📸 Newly uploaded files:", newlyUploaded);

      // Add to session files (not replace - allow multiple uploads)
      setSessionFiles(prev => [...prev, ...newlyUploaded]);

    } catch (e) {
      console.error("Upload failed:", e);
      alert("Upload failed: " + (e?.response?.data?.message || e?.message));
    }
  };

  /**
   * Handle native camera capture
   * Upload captured files and add to session files
   * @param {File[]} capturedFiles - Array of File objects from NativeCameraCapture
   */
  const handleNativeCameraCapture = async (capturedFiles) => {
    console.log('📸 Captured files from native camera:', capturedFiles);
    
    // Close the camera modal
    setShowCamera(false);
    
    if (!capturedFiles?.length || !timelineEvent?.id) {
      console.log('No files captured or no timeline event');
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData and append all captured files
      const fd = new FormData();
      capturedFiles.forEach((file) => {
        fd.append("files", file, file.name);
      });

      console.log('📤 Uploading captured files to timeline event:', timelineEvent.id);

      // Upload using existing endpoint
      const response = await axios.post(
        `/api/events/${tenant}/events/${timelineEvent.id}/uploadimage`,
        fd,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('Upload progress:', percentCompleted + '%');
          }
        }
      );

      console.log('✅ Upload successful:', response.data);

      // Use the uploaded file paths returned by the server
      const uploaded = response.data?.uploaded || [];

      const newFiles = uploaded.map(media => ({
        name: media.filename || media.path?.split('/').pop() || 'camera',
        url: media.path,
        type: media.mimetype || 'image/jpeg',
        mimetype: media.mimetype || 'image/jpeg'
      }));

      console.log('📸 Newly captured files:', newFiles);

      // Add to session files
      setSessionFiles(prev => [...prev, ...newFiles]);

      console.log('✅ Files captured and uploaded successfully!');
      
    } catch (error) {
      console.error('❌ Upload failed:', error);
      
      // Show error to user
      const errorMessage = error?.response?.data?.message || 
                          error?.message || 
                          'Failed to upload captured media';
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  // Handle Post Now - ONLY post session files
  const handlePostNow = () => {
    setPostChoiceOpen(false);

    if (sessionFiles.length === 0) {
      alert("Please upload or capture media first.");
      return;
    }

    // Build arrays from ONLY session files
    const selectedImages = sessionFiles
      .filter(f => isImage(f) || f.mimetype?.startsWith('image/'))
      .map(f => toPublic(f.url));
    
    const selectedVideos = sessionFiles
      .filter(f => isVideo(f) || f.mimetype?.startsWith('video/'))
      .map(f => toPublic(f.url));

    const cover = selectedImages[0] || "";

    console.log("🖼️ Posting images:", selectedImages);
    console.log("🎥 Posting videos:", selectedVideos);
    console.log("📋 Total session files:", sessionFiles.length);

    navigate(`/${tenant}/${timelineEvent.id}/mediaPost`, {
      state: {
        context: "timeline",
        tenant,
        eventId: timelineEvent.id,
        title: timelineEvent?.name || "",
        description: timelineEvent?.description || "",
        image: cover,
        images: selectedImages,
        videos: selectedVideos,
        event: timelineEvent,
      },
      replace: false,
    });
  };

  // Handle Schedule - ONLY schedule session files
  const handleSchedule = () => {
    setPostChoiceOpen(false);

    if (sessionFiles.length === 0) {
      alert("Please upload or capture media first.");
      return;
    }

    const selectedImages = sessionFiles
      .filter(f => isImage(f) || f.mimetype?.startsWith('image/'))
      .map(f => toPublic(f.url));
    
    const selectedVideos = sessionFiles
      .filter(f => isVideo(f) || f.mimetype?.startsWith('video/'))
      .map(f => toPublic(f.url));

    navigate(`/${tenant}/${timelineEvent.id}/schedulePost`, {
      state: {
        context: "timeline",
        tenant,
        eventId: timelineEvent.id,
        title: timelineEvent?.name || "",
        description: timelineEvent?.description || "",
        images: selectedImages,
        videos: selectedVideos,
        event: timelineEvent,
      },
      replace: false,
    });
  };

  // Handle subtitle editor save
  const handleSubtitleSaved = (vttUrl) => {
    setVideoSubtitles(prev => {
      const copy = [...prev];
      copy[editingVideoIdx] = vttUrl;
      return copy;
    });
    setEditingVideoIdx(null);
  };

  // Clear session files
  const handleClear = () => {
    if (window.confirm("Clear all media from this post?")) {
      setSessionFiles([]);
    }
  };

  const footerButtons = [
    {
      id: "back",
      icon: <FaArrowLeft />,
      label: "Back",
      onClick: () => navigate(`/${tenant}/post`),
      disabled: loading || uploading,
    },
    {
      id: "camera",
      icon: <FaCamera />,
      label: "Camera",
      onClick: () => setShowCamera(true),
      disabled: loading || uploading || !timelineEvent?.id,
    },
    {
      id: "upload",
      icon: <FaUpload />,
      label: "Upload",
      onClick: () => setShowUpload(true),
      disabled: loading || uploading || !timelineEvent?.id,
    },
    {
      id: "post",
      icon: <FaPaperPlane />,
      label: "Post",
      onClick: () => setPostChoiceOpen(true),
      disabled: loading || uploading || sessionFiles.length === 0,
    },
    {
      id: "clear",
      icon: <FaTrash />,
      label: "Clear",
      onClick: handleClear,
      disabled: loading || uploading || sessionFiles.length === 0,
    },
  ];

  if (loading) {
    return (
      <div className="container">
        <h2 className="hdr hdr--primary hdr--lg">New Post</h2>
        <div>Loading...</div>
        <AppFooter buttons={footerButtons} />
      </div>
    );
  }

  const images = sessionFiles.filter(f => isImage(f) || f.mimetype?.startsWith('image/'));
  const videos = sessionFiles.filter(f => isVideo(f) || f.mimetype?.startsWith('video/'));

  return (
    <div className="container">
      <h2 className="hdr hdr--primary hdr--lg">New Post</h2>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(`/${tenant}/post`)} disabled={loading || uploading}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/post/text`)} disabled={loading || uploading}>
          <FaFont /> Text Post
        </button>
        <button type="button" onClick={() => setShowCamera(true)} disabled={loading || uploading || !timelineEvent?.id}>
          <FaCamera /> Camera
        </button>
        <button type="button" onClick={() => setShowUpload(true)} disabled={loading || uploading || !timelineEvent?.id}>
          <FaUpload /> Upload
        </button>
        <button type="button" className="btn-primary-action" onClick={() => setPostChoiceOpen(true)} disabled={loading || uploading || sessionFiles.length === 0}>
          <FaPaperPlane /> Post
        </button>
        <button type="button" className="btn-danger-action" onClick={handleClear} disabled={loading || uploading || sessionFiles.length === 0}>
          <FaTrash /> Clear
        </button>
      </div>

      <div className="desktop-two-col">
        {/* Left column: text/inputs */}
        <div className="desktop-two-col__form">
          {/* Show upload progress indicator */}
          {uploading && (
            <div className="alert alert-info" style={{ marginBottom: '1rem' }}>
              Uploading media files...
            </div>
          )}

          {!uploading && (
            <button
              type="button"
              className="alert alert-info"
              style={{ width: '100%', cursor: 'pointer', border: 'none', textAlign: 'center' }}
              onClick={() => setShowMediaModal(true)}
              disabled={loading || uploading || !timelineEvent?.id}
            >
              Upload or capture media for your new post
            </button>
          )}

          <div className="form-group">
            <label>Media ({images.length} photos, {videos.length} videos)</label>
            {sessionFiles.length === 0 && (
              <div className="text-gray-400">No media yet. Use Camera or Upload.</div>
            )}
          </div>
        </div>

        {/* Right column: media */}
        <div className="desktop-two-col__media">
          {sessionFiles.length > 0 && (
            <>
              {/* Images */}
              {images.length > 0 && (
                <div className="mb-3">
                  {images.map((file, i) => (
                    <div
                      key={i}
                      className="border rounded overflow-hidden"
                      style={{ background: "#111", marginBottom: 10 }}
                    >
                      <img
                        src={toAbs(baseUrl, file.url)}
                        alt=""
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                          objectFit: "contain",
                        }}
                        onError={(e) => (e.currentTarget.src = toAbs(baseUrl, "/public/app/default-image.png"))}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Videos with subtitle support */}
              {videos.length > 0 && (
                <div className="mb-3">
                  {videos.map((file, i) => (
                    <div key={file.url || i} style={{ marginBottom: 10, position: "relative" }}>
                      <video
                        src={toAbs(baseUrl, file.url)}
                        controls
                        style={{ width: "100%", borderRadius: 8 }}
                      >
                        {videoSubtitles[i] && (
                          <track
                            kind="subtitles"
                            src={videoSubtitles[i]}
                            srcLang="en"
                            label="English"
                            default
                          />
                        )}
                      </video>
                      <button
                        type="button"
                        onClick={() => setEditingVideoIdx(i)}
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          background: "rgba(0,0,0,0.6)",
                          color: "#fff",
                          border: "none",
                          borderRadius: 6,
                          padding: "6px 12px",
                          fontSize: 13,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                        title="Edit subtitles"
                      >
                        <FaClosedCaptioning /> {videoSubtitles[i] ? 'Edit Subtitles' : 'Subtitles'}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Native Camera Capture Component */}
      {showCamera && (
        <NativeCameraCapture
          mode="both"              // Allow both photo and video capture
          multiple={true}          // Allow multiple captures
          maxFiles={10}            // Maximum 10 files per session
          maxImageSizeMB={10}      // Max 10MB per image
          maxVideoSizeMB={100}     // Max 100MB per video
          onCapture={handleNativeCameraCapture}
          onClose={() => setShowCamera(false)}
        />
      )}

      {showUpload && (
        <ImageUploadModal
          onClose={() => setShowUpload(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {postChoiceOpen && (
        <PostChoiceModal
          isOpen={postChoiceOpen}
          onClose={() => setPostChoiceOpen(false)}
          onPostNow={handlePostNow}
          onSchedule={handleSchedule}
        />
      )}

      {/* Subtitle Editor */}
      {editingVideoIdx !== null && videos[editingVideoIdx] && (
        <SubtitleEditor
          src={toAbs(baseUrl, videos[editingVideoIdx].url)}
          tenantId={Number(tenant) || 1}
          eventId={timelineEvent?.id ? Number(timelineEvent.id) : null}
          existingVttUrl={videoSubtitles[editingVideoIdx] || null}
          onSaved={handleSubtitleSaved}
          onClose={() => setEditingVideoIdx(null)}
        />
      )}

      {showMediaModal && (
        <div className="modal-overlay" onClick={() => setShowMediaModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="hdr hdr--primary hdr--md" style={{ marginBottom: '1rem' }}>Add Media</h3>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                type="button"
                className="btn btn--primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', fontSize: '1rem' }}
                onClick={() => { setShowMediaModal(false); setShowCamera(true); }}
              >
                <FaCamera /> Camera
              </button>
              <button
                type="button"
                className="btn btn--primary"
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.5rem', fontSize: '1rem' }}
                onClick={() => { setShowMediaModal(false); setShowUpload(true); }}
              >
                <FaUpload /> Upload
              </button>
            </div>
            <button
              type="button"
              className="btn btn--secondary"
              style={{ marginTop: '1rem', width: '100%' }}
              onClick={() => setShowMediaModal(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <AppFooter buttons={footerButtons} />
    </div>
  );
};

export default NewPost;